/**
 * SummaryTable — sommaire FPZ-500 d'une période : montants calculés, ajustements manuels (lignes 101 · 103 · 106 · 203 · 206) et montants déclarés.
 * « Corriger les montants » → saisie d'un ajustement (+/−) et d'un motif par ligne ; les lignes nettes et le total se recalculent.
 */
import { useState } from "react";
import { AlertTriangle, ClipboardCheck, Loader2, Pencil, RotateCcw, Save, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { r2 } from "@/lib/format";
import { money, signedMoney } from "./_shared";

const LINES = [
  ["101", "Ventes (sans taxes)", "line_101_sales"],
  ["103", "TPS perçue", "line_103_tps_collected"],
  ["106", "CTI (TPS payée)", "line_106_cti"],
  ["109", "TPS nette", "line_109_tps_net", true],
  ["203", "TVQ perçue", "line_203_tvq_collected"],
  ["206", "RTI (TVQ payée)", "line_206_rti"],
  ["209", "TVQ nette", "line_209_tvq_net", true],
];
const ADJUSTABLE = new Set(["101", "103", "106", "203", "206"]);
const inp = "border rounded-md px-2 py-1 text-xs w-full";

function preview(computed, draft) {
  const v = (c) => r2((computed[c] || 0) + (parseFloat(draft[c]?.amount) || 0));
  const l = { 101: v("101"), 103: v("103"), 106: v("106"), 203: v("203"), 206: v("206") };
  l[109] = r2(l[103] - l[106]); l[209] = r2(l[203] - l[206]);
  return { lines: l, net: r2(l[109] + l[209]) };
}

export function SummaryTable({ period, totals, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const adj = totals.adjustments || {};
  const computed = totals.computed || {};
  const showCols = editing || totals.has_adjustments;
  const filed = period.filing && period.filing.status !== "to_file";
  const pv = editing ? preview(computed, draft) : null;
  const value = (code, key) => (pv ? pv.lines[code] : totals[key]);
  const net = pv ? pv.net : totals.net_total;

  const start = () => { setDraft(Object.fromEntries([...ADJUSTABLE].map((c) => [c, { amount: adj[c]?.amount ?? "", note: adj[c]?.note || "" }]))); setEditing(true); };
  const setD = (c, patch) => setDraft((p) => ({ ...p, [c]: { ...p[c], ...patch } }));
  const save = async (reset = false) => {
    if (reset && !window.confirm("Retirer tous les ajustements de cette période ? Les montants calculés seront déclarés.")) return;
    setSaving(true);
    try {
      const lines = reset ? {} : Object.fromEntries(Object.entries(draft).filter(([, v]) => Math.abs(parseFloat(v.amount) || 0) >= 0.005).map(([c, v]) => [c, { amount: r2(v.amount), note: v.note }]));
      await api.put(`/admin/cabinet-taxes/periods/${period.key}/adjustments`, { lines });
      toast.success(reset ? "Ajustements retirés" : "Montants corrigés");
      setEditing(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid={`summary-${period.key}`}>
      <div className="px-4 py-2 bg-slate-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2">
        <ClipboardCheck className="w-3.5 h-3.5" /> Sommaire FPZ-500 — {period.label}
        {!editing && <button onClick={start} className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal font-semibold text-[11px] bg-white/10 hover:bg-white/20 rounded-md px-2 py-1" data-testid={`summary-edit-${period.key}`}><Pencil className="w-3 h-3" /> Corriger les montants</button>}
        {!editing && totals.has_adjustments && <span className="text-[10px] bg-amber-400 text-amber-950 rounded-full px-2 py-0.5" data-testid={`summary-adjusted-${period.key}`}>Ajusté</span>}
      </div>
      {editing && filed && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200 px-4 py-2" data-testid={`summary-filed-warning-${period.key}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Cette déclaration est déjà marquée {period.filing.status === "paid" ? "payée" : "produite"} : une modification des montants peut exiger une déclaration modifiée auprès de Revenu Québec.
        </div>
      )}
      <table className="w-full text-sm">
        {showCols && (
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr><th className="px-4 py-1.5 text-left w-14">Ligne</th><th className="px-2 py-1.5 text-left">Description</th><th className="px-2 py-1.5 text-right w-28">Calculé</th><th className="px-2 py-1.5 text-right w-36">Ajustement</th><th className="px-4 py-1.5 text-right w-32">Déclaré</th></tr>
          </thead>
        )}
        <tbody>
          {LINES.map(([code, label, key, bold]) => {
            const a = adj[code];
            const bd = totals.breakdown?.[code];
            return (
              <tr key={code} className={`border-t align-top ${bold ? "bg-slate-50 font-semibold" : ""}`}>
                <td className="px-4 py-1.5 w-14 font-mono text-xs text-slate-500">{code}</td>
                <td className="px-2 py-1.5">{label}{!editing && a?.note && <div className="text-[11px] font-normal italic text-amber-700">{a.note}</div>}
                  {bd && Math.abs(bd.gl || 0) >= 0.005 && <div className="text-[11px] font-normal text-slate-500" data-testid={`breakdown-${code}-${period.key}`}>dont pièces {signedMoney(bd.docs)} · écritures hors pièces (journal général) <span className={bd.gl < 0 ? "text-rose-700 font-semibold" : ""}>{signedMoney(bd.gl)}</span></div>}
                </td>
                {showCols && <td className="px-2 py-1.5 text-right tabular-nums text-slate-500 font-normal" data-testid={`computed-${code}-${period.key}`}>{ADJUSTABLE.has(code) ? signedMoney(computed[code]) : ""}</td>}
                {showCols && (
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {editing && ADJUSTABLE.has(code) ? (
                      <div className="space-y-1">
                        <input type="number" step="0.01" value={draft[code]?.amount ?? ""} onChange={(e) => setD(code, { amount: e.target.value })} placeholder="0,00" className={`${inp} text-right font-mono`} data-testid={`adjust-amount-${code}-${period.key}`} />
                        <input value={draft[code]?.note || ""} onChange={(e) => setD(code, { note: e.target.value })} placeholder="Motif" maxLength={300} className={inp} data-testid={`adjust-note-${code}-${period.key}`} />
                      </div>
                    ) : (a ? <span className="text-amber-700" data-testid={`adjustment-${code}-${period.key}`}>{a.amount > 0 ? "+ " : ""}{signedMoney(a.amount)}</span> : "")}
                  </td>
                )}
                <td className="px-4 py-1.5 text-right tabular-nums" data-testid={`line-${code}-${period.key}`}>{signedMoney(value(code, key))}</td>
              </tr>
            );
          })}
          <tr className={`border-t text-base font-bold ${net >= 0 ? "bg-indigo-50 text-indigo-900" : "bg-emerald-50 text-emerald-900"}`}>
            <td className="px-4 py-2" />
            <td className="px-2 py-2" colSpan={showCols ? 3 : 1}>{net >= 0 ? "Montant à remettre" : "Remboursement à recevoir"}</td>
            <td className="px-4 py-2 text-right tabular-nums" data-testid={`net-total-${period.key}`}>{money(Math.abs(net))}</td>
          </tr>
        </tbody>
      </table>
      {editing && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-t bg-slate-50">
          {totals.has_adjustments ? <button onClick={() => save(true)} disabled={saving} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800" data-testid={`summary-reset-${period.key}`}><RotateCcw className="w-3.5 h-3.5" /> Retirer les ajustements</button> : <span className="text-[11px] text-slate-500">Ajustement positif = ajoute au calculé · négatif = retranche.</span>}
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 border rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-white" data-testid={`summary-cancel-${period.key}`}><X className="w-3.5 h-3.5" /> Annuler</button>
            <button onClick={() => save(false)} disabled={saving} className="inline-flex items-center gap-1 bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50" data-testid={`summary-save-${period.key}`}>{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer</button>
          </div>
        </div>
      )}
      {!editing && totals.has_adjustments && totals.adjusted_at && <div className="px-4 py-1.5 border-t text-[11px] text-slate-500">Ajusté le {String(totals.adjusted_at).slice(0, 10)}{totals.adjusted_by ? ` par ${totals.adjusted_by}` : ""}.</div>}
    </div>
  );
}
